// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0

/**
 * Organization Guard Component
 *
 * This component ensures that an organization is selected before allowing access
 * to protected routes. This is a critical security measure for HIPAA compliance,
 * ensuring all data access is properly scoped to the user's organization.
 */

import {
  Alert,
  Box,
  Button,
  Center,
  Container,
  Loader,
  Paper,
  Select,
  Stack,
  Text,
  Title,
} from '@mantine/core';
import { useMedplum } from '@medplum/react';
import { IconAlertCircle, IconBuilding, IconShieldCheck } from '@tabler/icons-react';
import type { JSX, ReactNode } from 'react';
import { useState } from 'react';
import { useOrganization } from '../contexts/OrganizationContext';

interface OrganizationGuardProps {
  children: ReactNode;
}

/**
 * Wraps protected content and ensures an organization is selected.
 * If no organization is selected, shows a selection screen.
 * If no organizations are available, shows an error message.
 */
export function OrganizationGuard({ children }: OrganizationGuardProps): JSX.Element {
  const medplum = useMedplum();
  const { organizations, currentOrganization, loading, requestOrganizationSwitch } = useOrganization();
  const [selectedOrgId, setSelectedOrgId] = useState<string | null>(null);

  const handleSignOut = (): void => {
    medplum.signOut();
    window.location.href = '/signin';
  };

  // Show loading state while fetching organizations
  if (loading) {
    return (
      <Center h="100vh">
        <Stack align="center" gap="md">
          <Loader size="lg" />
          <Text c="dimmed">Loading organization data...</Text>
        </Stack>
      </Center>
    );
  }

  // No organizations available - block access completely
  if (organizations.length === 0) {
    return (
      <Center h="100vh">
        <Container size="sm">
          <Paper p="xl" radius="md" withBorder>
            <Stack align="center" gap="lg">
              <IconAlertCircle size={64} color="var(--mantine-color-orange-6)" />
              <Title order={2} ta="center">
                No Practice Assigned
              </Title>
              <Text ta="center" size="lg">
                You do not have an assigned practice yet.
              </Text>
              <Text c="dimmed" ta="center">
                Your account has not been assigned to a practice/organization.
                Please contact your administrator or practice manager to get access.
              </Text>
              <Alert
                icon={<IconShieldCheck size={16} />}
                title="Why is this required?"
                color="blue"
                variant="light"
              >
                For HIPAA compliance and patient data security, all users must be assigned
                to a practice before they can access the application. This ensures that
                patient data is only visible to authorized personnel within your organization.
              </Alert>
              <Stack gap="xs" w="100%">
                <Button
                  variant="outline"
                  fullWidth
                  onClick={() => window.location.reload()}
                >
                  Check Again
                </Button>
                <Button
                  variant="subtle"
                  fullWidth
                  color="gray"
                  onClick={handleSignOut}
                >
                  Sign Out
                </Button>
              </Stack>
            </Stack>
          </Paper>
        </Container>
      </Center>
    );
  }

  // No organization selected - show selection screen
  if (!currentOrganization) {
    const handleSelectOrganization = (): void => {
      const org = organizations.find((o) => o.id === selectedOrgId);
      if (org) {
        requestOrganizationSwitch(org);
      }
    };

    return (
      <Center h="100vh">
        <Container size="sm">
          <Paper p="xl" radius="md" withBorder>
            <Stack gap="lg">
              <Box ta="center">
                <IconBuilding size={48} color="var(--mantine-color-purple-6)" />
              </Box>
              <Title order={2} ta="center">
                Select Your Practice
              </Title>
              <Text c="dimmed" ta="center">
                Please select the practice you want to work with. You can switch
                practices later from your profile menu.
              </Text>

              <Select
                label="Practice"
                placeholder="Select a practice"
                data={organizations.map((org) => ({
                  value: org.id || '',
                  label: org.name || 'Unnamed Practice',
                }))}
                value={selectedOrgId}
                onChange={setSelectedOrgId}
                searchable
                size="md"
              />

              <Alert
                icon={<IconShieldCheck size={16} />}
                title="Data Isolation"
                color="blue"
                variant="light"
              >
                Your data access will be limited to the selected practice. This ensures
                HIPAA compliance and patient data privacy.
              </Alert>

              <Button
                size="md"
                disabled={!selectedOrgId}
                onClick={handleSelectOrganization}
                leftSection={<IconBuilding size={18} />}
              >
                Continue
              </Button>
            </Stack>
          </Paper>
        </Container>
      </Center>
    );
  }

  // Organization is selected - render children
  return <>{children}</>;
}
